/**
 * ÁREA CLIENTE — MANTENIMIENTO (FASE 8.4)
 *
 *   /portal/mantenimiento                      Resumen + accesos
 *   /portal/mantenimiento/incidencias(/nueva|/:id)
 *   /portal/mantenimiento/partes(/:id)
 *   /portal/mantenimiento/activos(/:id)        (también destino del QR para CLIENTE)
 *   /portal/mantenimiento/proximos
 *
 * Pantallas DISTINTAS de las de gestión: móvil primero, tarjetas, sólo DTO
 * público (`/api/portal/mantenimiento/*`). Aquí no hay — ni puede haber,
 * porque el servidor no lo envía — técnicos, Planner, planes preventivos,
 * conflictos, autores internos ni textos internos. Módulo separado de
 * Calidad: no comparte incidencias ni pantallas.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  api,
  type ActivoPublico,
  type ActivoPublicoDetalle,
  type EvidenciaPublicaMnt,
  type HitoPublicoMnt,
  type IncidenciaMntPublica,
  type IncidenciaMntPublicaDetalle,
  type PartePublico,
  type PartePublicoDetalle,
  type RefPublica,
  type ResumenMantenimientoPublico,
} from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, fechaHora, Modal, Panel, StatusBadge, useDatos, Vacio } from '../componentes/base.tsx';
import { estadoActivo, estadoIncidenciaMnt, estadoPtMantenimiento, type Tono } from '../estados.ts';
import { abrirArchivoRuta, EnlaceVolver, programacion, useVolverAqui, type Volver } from '../componentes/mantenimiento.tsx';
import { CodigoQr } from '../componentes/qr.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { AvisoDrill, chipsDrill, DRILL_VACIO, TEXTO_INDICADOR_PT_PUBLICO } from '../componentes/indicadores-mnt.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { urlFicha } from '../qr.ts';

const RAIZ = '/portal/mantenimiento';
const MIGAS = [{ texto: 'Mantenimiento', a: RAIZ }];

const URGENCIAS: Readonly<Record<string, string>> = { BAJA: 'Baja', MEDIA: 'Media', ALTA: 'Alta', CRITICA: 'Crítica' };

//  UX-1C: el TEXTO lo da el servidor (redacción pensada para el cliente); el TONO, el mapa central (`estados.ts`),
//  el mismo que ve el equipo interno — sin paleta propia del área cliente.
const varianteEstadoIm = (estado: string): Tono => estadoIncidenciaMnt(estado).tone;
const varianteEstadoPt = (estado: string): Tono => estadoPtMantenimiento(estado).tone;
const Estado = ({ texto, variante }: { texto: string; variante: Tono }): ReactNode => <StatusBadge label={texto} tone={variante} />;

function Dato({ e, children }: { e: string; children: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{children}</div>
    </div>
  );
}

function useCentros(): RefPublica[] {
  return useDatos(() => api.get<{ filas: RefPublica[] }>('/api/portal/mantenimiento/centros'), []).datos?.filas ?? [];
}

/** Evolución pública: nunca nombres internos — «tú» o «el equipo de mantenimiento». */
function Evolucion({ hitos }: { hitos: readonly HitoPublicoMnt[] }): ReactNode {
  if (hitos.length === 0) return <p className="pequena tenue">Sin novedades todavía.</p>;
  return (
    <ol className="lista-nc-historial" aria-label="Evolución">
      {hitos.map((h, k) => (
        <li key={k}>
          <span className="pequena">{h.texto}</span>
          <span className="pequena tenue"> · {fechaHora(h.fecha)}{h.actor === 'TI' ? ' · por ti' : h.actor === 'EQUIPO' ? ' · equipo de mantenimiento' : ''}</span>
        </li>
      ))}
    </ol>
  );
}

function Evidencias({ evidencias, vacio = 'No hay fotos ni documentos publicados.' }: { evidencias: readonly EvidenciaPublicaMnt[]; vacio?: string }): ReactNode {
  if (evidencias.length === 0) return <p className="pequena tenue">{vacio}</p>;
  return (
    <ul className="lista-evidencias-mnt" aria-label="Fotos y documentos">
      {evidencias.map((e) => (
        <li key={e.id}>
          <button type="button" className="enlace" onClick={() => void abrirArchivoRuta(`/api/portal/mantenimiento/evidencias/${e.id}/archivo`)}>{e.nombreOriginal}</button>
          <span className="suave pequena"> {fechaHora(e.subidaEn)}{e.subidaPorTi ? ' · subida por ti' : ''}</span>
          {e.descripcion !== null && <div className="pequena">{e.descripcion}</div>}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------------ tarjetas

function TarjetaIncidencia({ i }: { i: IncidenciaMntPublica }): ReactNode {
  return (
    <Link to={`${RAIZ}/incidencias/${i.id}`} className="tarjeta-ot tarjeta-parte" aria-label={`${i.codigo} ${i.titulo}`}>
      <div className="tarjeta-ot-cabecera">
        <span className="tarjeta-ot-codigo mono">{i.codigo}</span>
        <Estado texto={i.estadoTexto} variante={varianteEstadoIm(i.estado)} />
      </div>
      <div className="tarjeta-ot-datos">
        <span className="tarjeta-ot-fecha">{i.titulo}</span>
        <span className="tarjeta-ot-secundario">{i.centro.nombre}{i.activo !== null ? ` · ${i.activo.nombre}` : ''}</span>
        <span className="tarjeta-ot-secundario">{fecha(i.creadaEn.slice(0, 10))}{i.partes.length > 0 ? ` · ${i.partes.map((p) => p.codigo).join(', ')}` : ''}</span>
      </div>
    </Link>
  );
}

function TarjetaParte({ p, volver }: { p: PartePublico; volver?: { volver: Volver } }): ReactNode {
  return (
    <Link to={`${RAIZ}/partes/${p.id}`} state={volver} className="tarjeta-ot tarjeta-parte" aria-label={`${p.codigo} ${p.titulo}`}>
      <div className="tarjeta-ot-cabecera">
        <span className="tarjeta-ot-codigo mono">{p.codigo}</span>
        <Estado texto={p.estadoTexto} variante={varianteEstadoPt(p.estado)} />
      </div>
      <div className="tarjeta-ot-datos">
        <span className="tarjeta-ot-fecha">{p.titulo}</span>
        <span className="tarjeta-ot-secundario">{p.tipo === 'PREVENTIVO' ? 'Preventivo' : p.tipoTexto} · {p.centro.nombre}</span>
        {p.activos.length > 0 && <span className="tarjeta-ot-secundario">{p.activos.map((a) => a.nombre).join(' · ')}</span>}
        <span className="tarjeta-ot-secundario">{programacion(p.fechaProgramada, p.horaProgramada)}{p.pendienteTexto !== null ? ` · ${p.pendienteTexto}` : ''}</span>
      </div>
    </Link>
  );
}

function TarjetaActivo({ a }: { a: ActivoPublico }): ReactNode {
  return (
    <div className="tarjeta-ot">
      <div className="tarjeta-ot-cabecera">
        <span className="tarjeta-ot-codigo mono">{a.codigo}</span>
        <Estado texto={a.estadoTexto} variante={estadoActivo(a.estado).tone} />
      </div>
      <div className="tarjeta-ot-datos">
        <span className="tarjeta-ot-fecha">{a.nombre}</span>
        <span className="tarjeta-ot-secundario">{a.tipo} · {a.centro.nombre}{a.ubicacion !== null ? ` · ${a.ubicacion}` : ''}</span>
        <span className="tarjeta-ot-secundario">Titularidad: {a.titularidadTexto}</span>
        <Link className="boton boton-tactil" to={`${RAIZ}/activos/${a.id}`} aria-label={`Ver activo ${a.nombre}`}>Ver activo</Link>
      </div>
    </div>
  );
}

function ListaProximos({ filas }: { filas: readonly PartePublico[] }): ReactNode {
  return (
    <ol className="lista-mis-partes" aria-label="Próximos mantenimientos">
      {filas.map((p) => (
        <li key={p.id}>
          <Link to={`${RAIZ}/partes/${p.id}`} className="tarjeta-ot tarjeta-parte" aria-label={`${programacion(p.fechaProgramada, p.horaProgramada)} ${p.titulo}`}>
            <div className="tarjeta-ot-cabecera">
              <span className="tarjeta-ot-fecha">{programacion(p.fechaProgramada, p.horaProgramada)}</span>
              <Estado texto={p.estadoTexto} variante={varianteEstadoPt(p.estado)} />
            </div>
            <div className="tarjeta-ot-datos">
              <span>{p.titulo}</span>
              <span className="tarjeta-ot-secundario">{p.centro.nombre}{p.activos.length > 0 ? ` · ${p.activos.map((a) => a.nombre).join(' · ')}` : ''}</span>
              <span className="tarjeta-ot-secundario mono">{p.codigo}</span>
            </div>
          </Link>
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------------------------------ inicio

export function PaginaPortalMantenimiento(): ReactNode {
  const { puede } = useSesion();
  const resumen = useDatos(() => api.get<ResumenMantenimientoPublico>('/api/portal/mantenimiento/resumen'), []);
  const plural = (n: number, uno: string, varios: string): string => (n === 1 ? uno : varios.replace('#', String(n)));
  return (
    <Shell titulo="Mantenimiento">
      <Panel estado={resumen}>
        {(r) => (
          <div className="panel">
            <div className="panel-cuerpo" aria-label="Resumen">
              <p style={{ margin: 0 }}>
                {r.incidenciasAbiertas === 0 ? 'No tienes incidencias abiertas.' : plural(r.incidenciasAbiertas, 'Tienes 1 incidencia abierta.', 'Tienes # incidencias abiertas.')}
              </p>
              <p className="pequena suave" style={{ margin: '4px 0 0' }}>
                {r.partesEnCurso === 0 && r.partesPendientes === 0 ? 'No hay trabajos en curso ni pendientes.'
                  : [r.partesEnCurso > 0 ? plural(r.partesEnCurso, '1 trabajo en curso', '# trabajos en curso') : null,
                    r.partesPendientes > 0 ? plural(r.partesPendientes, '1 trabajo pendiente', '# trabajos pendientes') : null].filter(Boolean).join(' · ') + '.'}
              </p>
            </div>
          </div>
        )}
      </Panel>
      <nav className="mnt-accesos" aria-label="Secciones de mantenimiento">
        {puede('mantenimiento:reportar_propio') && <Link className="mnt-acceso principal" to={`${RAIZ}/incidencias/nueva`}>Abrir incidencia</Link>}
        <Link className="mnt-acceso" to={`${RAIZ}/incidencias`}>Mis incidencias</Link>
        <Link className="mnt-acceso" to={`${RAIZ}/partes`}>Partes de trabajo</Link>
        <Link className="mnt-acceso" to={`${RAIZ}/activos`}>Activos</Link>
        <Link className="mnt-acceso" to={`${RAIZ}/proximos`}>Próximos mantenimientos</Link>
        <Link className="mnt-acceso" to={`${RAIZ}/indicadores`}>Indicadores</Link>
      </nav>
      {resumen.datos !== null && (
        <section className="panel" aria-label="Próximos">
          <div className="panel-cabecera"><h2>Próximos mantenimientos</h2></div>
          <div className="panel-cuerpo">
            {resumen.datos.proximos.length === 0 ? <p className="suave">No hay mantenimientos programados.</p> : <ListaProximos filas={resumen.datos.proximos} />}
          </div>
        </section>
      )}
    </Shell>
  );
}

// ------------------------------------------------------------------ incidencias

export function PaginaPortalIncidenciasMnt(): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const centros = useCentros();
  const [estado, setEstado] = useState('ABIERTAS');
  const [idCentro, setIdCentro] = useState('');
  const lista = useDatos(() => {
    const q = new URLSearchParams();
    if (estado !== '') q.set('estado', estado);
    if (idCentro !== '') q.set('idCentro', idCentro);
    return api.get<{ filas: IncidenciaMntPublica[] }>(`/api/portal/mantenimiento/incidencias?${q.toString()}`);
  }, [estado, idCentro]);
  return (
    <Shell migas={[...MIGAS, { texto: 'Incidencias' }]} titulo="Mis incidencias"
      acciones={puede('mantenimiento:reportar_propio') ? <Link className="boton principal" to={`${RAIZ}/incidencias/nueva`}>Abrir incidencia</Link> : undefined}>
      {/* F12.4: llegando desde Indicadores, la vuelta al mismo sitio. */}
      <EnlaceVolver />
      <div className="panel">
        <div className="panel-cuerpo fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-e`}>Estado</label>
            <select id={`${idBase}-e`} value={estado} onChange={(ev) => setEstado(ev.target.value)}>
              <option value="ABIERTAS">Abiertas</option>
              <option value="RESUELTA">Resueltas</option>
              <option value="CANCELADA">Canceladas</option>
              <option value="">Todas</option>
            </select>
          </div>
          {centros.length > 1 && (
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => setIdCentro(ev.target.value)}>
                <option value="">Todos</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? (
          <div className="panel"><Vacio titulo={estado === 'ABIERTAS' ? 'No tienes incidencias abiertas.' : 'No hay incidencias con ese filtro.'} /></div>
        ) : (
          <ul className="lista-mis-partes" aria-label="Incidencias">{d.filas.map((i) => <li key={i.id}><TarjetaIncidencia i={i} /></li>)}</ul>
        ))}
      </Panel>
    </Shell>
  );
}

export function PaginaPortalNuevaIncidenciaMnt(): ReactNode {
  const centros = useCentros();
  const [idCentro, setIdCentro] = useState('');
  const [idActivo, setIdActivo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [urgencia, setUrgencia] = useState('MEDIA');
  const [foto, setFoto] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [creada, setCreada] = useState<{ id: string; codigo: string; fotoFallida: boolean } | null>(null);
  const centro = idCentro !== '' ? idCentro : (centros.length === 1 ? centros[0]!.id : '');
  const activos = useDatos(
    () => (centro === '' ? Promise.resolve({ filas: [] as ActivoPublico[] }) : api.get<{ filas: ActivoPublico[] }>(`/api/portal/mantenimiento/activos?idCentro=${centro}`)),
    [centro],
  );

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<IncidenciaMntPublicaDetalle>('/api/portal/mantenimiento/incidencias', {
        idCentro: centro, ...(idActivo !== '' ? { idActivo } : {}), titulo: titulo.trim(), descripcion: descripcion.trim() || null, urgencia,
      });
      let fotoFallida = false;
      if (foto !== null) {
        try {
          const fd = new FormData();
          fd.set('contexto', 'GENERAL');
          fd.set('archivo', foto);
          await api.subir(`/api/portal/mantenimiento/incidencias/${r.id}/evidencias`, fd);
        } catch {
          fotoFallida = true;
        }
      }
      setCreada({ id: r.id, codigo: r.codigo, fotoFallida });
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (creada !== null) {
    return (
      <Shell migas={[...MIGAS, { texto: 'Nueva incidencia' }]} titulo="Incidencia enviada">
        <div className="panel">
          <div className="panel-cuerpo">
            <div className="nota exito" role="status"><strong>Incidencia {creada.codigo} registrada.</strong> El equipo de mantenimiento la revisará.</div>
            {creada.fotoFallida && <div className="nota aviso" role="status">La foto no se pudo adjuntar. Puedes añadirla desde la incidencia.</div>}
            <div className="fila"><Link className="boton principal" to={`${RAIZ}/incidencias/${creada.id}`}>Ver incidencia</Link><Link className="boton" to={RAIZ}>Volver a Mantenimiento</Link></div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell migas={[...MIGAS, { texto: 'Nueva incidencia' }]} titulo="Abrir incidencia">
      <div className="panel">
        <form className="panel-cuerpo" onSubmit={(ev) => void enviar(ev)} aria-label="Abrir incidencia">
          {centros.length !== 1 && (
            <div className="campo">
              <label htmlFor="pim-centro">Centro</label>
              <select id="pim-centro" required value={idCentro} onChange={(ev) => { setIdCentro(ev.target.value); setIdActivo(''); }}>
                <option value="">Elige el centro…</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <div className="campo">
            <label htmlFor="pim-activo">Equipo afectado (opcional)</label>
            <select id="pim-activo" value={idActivo} disabled={centro === ''} onChange={(ev) => setIdActivo(ev.target.value)}>
              <option value="">No lo sé / general</option>
              {/* 0076: el equipo de la empresa prestadora no se gestiona desde aquí (MNT-DEBT-ACTIVOS-EMPRESA). */}
              {(activos.datos?.filas ?? []).filter((a) => a.titularidad !== 'EMPRESA').map((a) => <option key={a.id} value={a.id}>{a.nombre} ({a.codigo})</option>)}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="pim-titulo">¿Qué ocurre?</label>
            <input id="pim-titulo" required minLength={3} maxLength={200} value={titulo} placeholder="p. ej. El aire acondicionado no enfría" onChange={(ev) => setTitulo(ev.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="pim-descripcion">Descripción (opcional)</label>
            <textarea id="pim-descripcion" rows={3} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="pim-urgencia">Urgencia</label>
            <select id="pim-urgencia" value={urgencia} onChange={(ev) => setUrgencia(ev.target.value)}>
              {Object.entries(URGENCIAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
            </select>
            <p className="suave pequena">Es la urgencia que indicas; el equipo de mantenimiento puede ajustarla al revisarla.</p>
          </div>
          <div className="campo">
            <label htmlFor="pim-foto">Adjuntar foto (opcional)</label>
            <input id="pim-foto" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(ev) => setFoto(ev.target.files?.[0] ?? null)} />
          </div>
          {error !== null && <ErrorCaja error={error} />}
          <button type="submit" className="principal grande" disabled={enviando || centro === '' || titulo.trim().length < 3}>{enviando ? 'Enviando…' : 'Enviar incidencia'}</button>
        </form>
      </div>
    </Shell>
  );
}

export function PaginaPortalIncidenciaMnt(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const d = useDatos(() => api.get<IncidenciaMntPublicaDetalle>(`/api/portal/mantenimiento/incidencias/${encodeURIComponent(id)}`), [id]);
  const [foto, setFoto] = useState<File | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [subiendo, setSubiendo] = useState(false);
  const migas = [...MIGAS, { texto: 'Incidencias', a: `${RAIZ}/incidencias` }, { texto: d.datos?.codigo ?? 'Incidencia' }];
  if (d.cargando && d.datos === null) return <Shell titulo="Incidencia" migas={migas}><Cargando /></Shell>;
  if (d.error !== null || d.datos === null) {
    return <Shell titulo="Incidencia" migas={migas}><div className="panel"><Vacio titulo="Esta incidencia no existe o no está disponible."><Link to={`${RAIZ}/incidencias`}>Volver</Link></Vacio></div></Shell>;
  }
  const i = d.datos;

  async function subir(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (foto === null) return;
    setSubiendo(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.set('contexto', 'GENERAL');
      fd.set('archivo', foto);
      await api.subir(`/api/portal/mantenimiento/incidencias/${i.id}/evidencias`, fd);
      setFoto(null);
      d.recargar();
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <Shell titulo={i.titulo} migas={migas}
      subtitulo={<span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}><span className="mono">{i.codigo}</span><Estado texto={i.estadoTexto} variante={varianteEstadoIm(i.estado)} /></span>}>
      {/* F12.2: si estaba resuelta y se ha reabierto el trabajo, se dice claro (sin detalles internos). */}
      {i.vueltaARevision === true && (
        <div className="nota aviso" role="status"><strong>Esta incidencia ha vuelto a revisión.</strong> Estamos trabajando de nuevo en ella; te contaremos el resultado aquí.</div>
      )}
      <div className="panel">
        <div className="panel-cuerpo">
          {i.descripcion !== null && <p style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{i.descripcion}</p>}
          <div className="datos">
            <Dato e="Centro">{i.centro.nombre}</Dato>
            <Dato e="Equipo">{i.activo === null ? 'General' : <Link to={`${RAIZ}/activos/${i.activo.id}`}>{i.activo.nombre}</Link>}</Dato>
            <Dato e="Urgencia">{URGENCIAS[i.urgencia] ?? i.urgencia}</Dato>
            <Dato e="Registrada">{fechaHora(i.creadaEn)}{i.creadaPorTi ? ' · por ti' : ''}</Dato>
            {i.resolucion !== null && <Dato e="Resolución">{i.resolucion}</Dato>}
          </div>
        </div>
      </div>
      {i.partes.length > 0 && (
        <section className="panel" aria-label="Partes de trabajo">
          <div className="panel-cabecera"><h2>Parte de trabajo generado</h2></div>
          <ul className="panel-cuerpo">
            {i.partes.map((p) => <li key={p.id}><Link className="mono" to={`${RAIZ}/partes/${p.id}`}>{p.codigo}</Link> · {p.estadoTexto}</li>)}
          </ul>
        </section>
      )}
      <section className="panel" aria-label="Fotos y documentos">
        <div className="panel-cabecera"><h2>Fotos y documentos</h2></div>
        <div className="panel-cuerpo">
          <Evidencias evidencias={i.evidencias} vacio="Sin fotos todavía." />
          {i.admiteEvidencias && puede('mantenimiento:reportar_propio') && (
            <form className="form-evidencia-mnt" onSubmit={(ev) => void subir(ev)} aria-label="Añadir foto">
              <div className="campo">
                <label htmlFor="pim-mas-foto">Añadir foto</label>
                <input id="pim-mas-foto" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={(ev) => setFoto(ev.target.files?.[0] ?? null)} />
              </div>
              {error !== null && <ErrorCaja error={error} />}
              <button type="submit" className="principal" disabled={foto === null || subiendo}>{subiendo ? 'Subiendo…' : 'Subir foto'}</button>
            </form>
          )}
        </div>
      </section>
      <section className="panel" aria-label="Evolución">
        <div className="panel-cabecera"><h2>Evolución</h2></div>
        <div className="panel-cuerpo"><Evolucion hitos={i.evolucion} /></div>
      </section>
    </Shell>
  );
}

// ------------------------------------------------------------------ partes

export function PaginaPortalPartes(): ReactNode {
  const idBase = useId();
  const centros = useCentros();
  //  F12.4: un indicador del área del cliente trae aquí su filtro exacto (y el centro) en la URL, con `volver`.
  const drill = useListaUrl({ centro: '', activo: '', activoNombre: '', ...DRILL_VACIO });
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [idCentro, setIdCentro] = useState(drill.valor('centro'));
  const claves = [['indicador', 'indicador'], ['pDesde', 'pDesde'], ['pHasta', 'pHasta'], ['idUbicacion', 'idUbicacion'], ['activo', 'idActivo']] as const;
  const filtroDrill = claves.map(([k]) => drill.valor(k)).join('|');
  const lista = useDatos(() => {
    const q = new URLSearchParams();
    if (estado !== '') q.set('estado', estado);
    if (tipo !== '') q.set('tipo', tipo);
    if (idCentro !== '') q.set('idCentro', idCentro);
    for (const [k, param] of claves) if (drill.valor(k) !== '') q.set(param, drill.valor(k));
    return api.get<{ filas: PartePublico[] }>(`/api/portal/mantenimiento/partes?${q.toString()}`);
  }, [estado, tipo, idCentro, filtroDrill]);
  const chips = chipsDrill((k) => drill.valor(k), TEXTO_INDICADOR_PT_PUBLICO);
  if (drill.valor('activo') !== '') chips.push({ clave: 'a', texto: `Equipo: ${drill.valor('activoNombre') || 'elegido'}`, quitar: { activo: '', activoNombre: '' } });
  return (
    <Shell migas={[...MIGAS, { texto: 'Partes de trabajo' }]} titulo="Partes de trabajo">
      {(drill.valor('volver') !== '' || chips.length > 0) && (
        <div className="panel"><div className="panel-cuerpo">
          <AvisoDrill volver={drill.valor('volver')} chips={chips} quitar={(c) => drill.fijarVarios(c.quitar as Partial<Record<'centro' | 'activo' | 'activoNombre' | keyof typeof DRILL_VACIO, string>>)} />
        </div></div>
      )}
      <div className="panel">
        <div className="panel-cuerpo fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-e`}>Estado</label>
            <select id={`${idBase}-e`} value={estado} onChange={(ev) => setEstado(ev.target.value)}>
              <option value="">Todos</option>
              <option value="ACTIVOS">En marcha</option>
              <option value="REABIERTO">Reabiertos</option>
              <option value="FINALIZADO">Trabajo finalizado</option>
              <option value="CERRADO">Cerrados</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-t`}>Tipo</label>
            <select id={`${idBase}-t`} value={tipo} onChange={(ev) => setTipo(ev.target.value)}>
              <option value="">Todos</option>
              <option value="PREVENTIVO">Preventivo</option>
              <option value="CORRECTIVO">Reparación</option>
              <option value="REVISION">Revisión</option>
              <option value="INSTALACION">Instalación</option>
            </select>
          </div>
          {centros.length > 1 && (
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => setIdCentro(ev.target.value)}>
                <option value="">Todos</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? (
          <div className="panel"><Vacio titulo="No hay partes visibles." /></div>
        ) : (
          <ul className="lista-mis-partes" aria-label="Partes de trabajo">{d.filas.map((p) => <li key={p.id}><TarjetaParte p={p} /></li>)}</ul>
        ))}
      </Panel>
    </Shell>
  );
}

export function PaginaPortalParte(): ReactNode {
  const { id = '' } = useParams();
  const d = useDatos(() => api.get<PartePublicoDetalle>(`/api/portal/mantenimiento/partes/${encodeURIComponent(id)}`), [id]);
  const migas = [...MIGAS, { texto: 'Partes de trabajo', a: `${RAIZ}/partes` }, { texto: d.datos?.codigo ?? 'Parte' }];
  if (d.cargando && d.datos === null) return <Shell titulo="Parte de trabajo" migas={migas}><Cargando /></Shell>;
  if (d.error !== null || d.datos === null) {
    return <Shell titulo="Parte de trabajo" migas={migas}><div className="panel"><Vacio titulo="Este parte no existe o no está disponible."><Link to={`${RAIZ}/partes`}>Volver</Link></Vacio></div></Shell>;
  }
  const p = d.datos;
  return (
    <Shell titulo={p.titulo} migas={migas}
      subtitulo={<span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}><span className="mono">{p.codigo}</span><Estado texto={p.estadoTexto} variante={varianteEstadoPt(p.estado)} /><span className="etiqueta neutra">{p.tipoTexto}</span></span>}>
      <EnlaceVolver />
      {p.estado === 'PENDIENTE' && <div className="nota aviso" role="status"><strong>{p.pendienteTexto ?? 'Pendiente'}.</strong></div>}
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="datos">
            <Dato e="Centro">{p.centro.nombre}</Dato>
            <Dato e="Equipos">{p.activos.length === 0 ? 'General' : p.activos.map((a, k) => <span key={a.id}>{k > 0 ? ', ' : ''}<Link to={`${RAIZ}/activos/${a.id}`}>{a.nombre}</Link></span>)}</Dato>
            <Dato e="Fecha programada">{programacion(p.fechaProgramada, p.horaProgramada)}</Dato>
            {p.duracionAproxMin !== null && <Dato e="Duración aproximada">{p.duracionAproxMin} min</Dato>}
            {p.programadoEnPlan && <Dato e="Origen">Programado dentro del plan de mantenimiento</Dato>}
            {p.cerradoEn !== null && <Dato e="Cerrado">{fechaHora(p.cerradoEn)}</Dato>}
          </div>
          {p.resumen !== null && <p style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{p.resumen}</p>}
        </div>
      </div>
      {p.incidencias.length > 0 && (
        <section className="panel" aria-label="Incidencia relacionada">
          <div className="panel-cabecera"><h2>Incidencia relacionada</h2></div>
          <ul className="panel-cuerpo">
            {p.incidencias.map((i) => <li key={i.id}><Link className="mono" to={`${RAIZ}/incidencias/${i.id}`}>{i.codigo}</Link> — {i.titulo}</li>)}
          </ul>
        </section>
      )}
      {p.visitas.length > 0 && (
        <section className="panel" aria-label="Visitas">
          <div className="panel-cabecera"><h2>Visitas</h2></div>
          <ul className="panel-cuerpo">
            {p.visitas.map((v, k) => <li key={k}>{fecha(v.fecha)} · {v.estadoTexto}{v.resultadoTexto !== null ? ` · ${v.resultadoTexto}` : ''}</li>)}
          </ul>
        </section>
      )}
      <section className="panel" aria-label="Fotos y documentos">
        <div className="panel-cabecera"><h2>Fotos y documentos</h2></div>
        <div className="panel-cuerpo"><Evidencias evidencias={p.evidencias} /></div>
      </section>
      <section className="panel" aria-label="Evolución">
        <div className="panel-cabecera"><h2>Evolución</h2></div>
        <div className="panel-cuerpo"><Evolucion hitos={p.evolucion} /></div>
      </section>
    </Shell>
  );
}

// ------------------------------------------------------------------ activos

export function PaginaPortalActivos(): ReactNode {
  const idBase = useId();
  const centros = useCentros();
  const [idCentro, setIdCentro] = useState('');
  const [texto, setTexto] = useState('');
  const lista = useDatos(() => {
    const q = new URLSearchParams();
    if (idCentro !== '') q.set('idCentro', idCentro);
    if (texto.trim() !== '') q.set('texto', texto.trim());
    return api.get<{ filas: ActivoPublico[] }>(`/api/portal/mantenimiento/activos?${q.toString()}`);
  }, [idCentro, texto]);
  return (
    <Shell migas={[...MIGAS, { texto: 'Activos' }]} titulo="Activos">
      <div className="panel">
        <div className="panel-cuerpo fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <input type="search" aria-label="Buscar activo" placeholder="Buscar equipo…" value={texto} onChange={(ev) => setTexto(ev.target.value)} />
          {centros.length > 1 && (
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => setIdCentro(ev.target.value)}>
                <option value="">Todos</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? (
          <div className="panel"><Vacio titulo="No hay activos registrados en tus centros." /></div>
        ) : (
          <ul className="lista-mis-partes" aria-label="Activos">{d.filas.map((a) => <li key={a.id}><TarjetaActivo a={a} /></li>)}</ul>
        ))}
      </Panel>
    </Shell>
  );
}

export function PaginaPortalActivo(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const d = useDatos(() => api.get<ActivoPublicoDetalle>(`/api/portal/mantenimiento/activos/${encodeURIComponent(id)}`), [id]);
  const migas = [...MIGAS, { texto: 'Activos', a: `${RAIZ}/activos` }, { texto: d.datos?.codigo ?? 'Activo' }];
  const [solicitar, setSolicitar] = useState(false);
  const [enviada, setEnviada] = useState<{ id: string; codigo: string } | null>(null);
  const volverAqui = useVolverAqui(d.datos?.nombre ?? 'el equipo');
  if (d.cargando && d.datos === null) return <Shell titulo="Activo" migas={migas}><Cargando /></Shell>;
  if (d.error !== null || d.datos === null) {
    return <Shell titulo="Activo" migas={migas}><div className="panel"><Vacio titulo="Este activo no existe o no está disponible."><Link to={`${RAIZ}/activos`}>Volver</Link></Vacio></div></Shell>;
  }
  const a = d.datos;
  return (
    <Shell titulo={a.nombre} migas={migas}
      subtitulo={<span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}><span className="mono">{a.codigo}</span><Estado texto={a.estadoTexto} variante={estadoActivo(a.estado).tone} /></span>}
      acciones={
        <span className="fila" style={{ flexWrap: 'wrap' }}>
          <Link className="boton" to={`${RAIZ}/activos/${a.id}/etiqueta`}>Etiqueta QR</Link>
          {/* F12.2: también sobre el equipo de la empresa prestadora que está en su centro. */}
          {puede('mantenimiento:reportar_propio') && a.puedeSolicitar !== false && a.estado !== 'BAJA' && (
            <button type="button" className="principal" onClick={() => { setEnviada(null); setSolicitar(true); }}>Solicitar mantenimiento</button>
          )}
        </span>
      }>
      <EnlaceVolver />
      {enviada !== null && (
        <div className="nota exito" role="status">
          <strong>Solicitud enviada.</strong> Hemos abierto el parte {enviada.codigo}: el equipo de mantenimiento lo planificará.{' '}
          <Link to={`${RAIZ}/partes/${enviada.id}`} state={volverAqui}>Ver el parte</Link>
        </div>
      )}
      <div className="panel">
        <div className="panel-cuerpo datos">
          <Dato e="Titularidad">{a.titularidad === 'EMPRESA' ? <span className="etiqueta info">{a.titularidadTexto}</span> : a.titularidadTexto}</Dato>
          <Dato e="Tipo">{a.tipo}</Dato>
          <Dato e="Centro">{a.centro.nombre}</Dato>
          <Dato e="Ubicación">{a.ubicacion ?? '—'}</Dato>
          <Dato e="Fabricante">{a.fabricante ?? '—'}</Dato>
          <Dato e="Modelo">{a.modelo ?? '—'}</Dato>
          {a.referenciaCliente !== null && <Dato e="Tu referencia">{a.referenciaCliente}</Dato>}
          {a.fechaInstalacion !== null && <Dato e="Instalado">{fecha(a.fechaInstalacion)}</Dato>}
        </div>
      </div>
      <section className="panel" aria-label="Próximos mantenimientos del activo">
        <div className="panel-cabecera"><h2>Próximos mantenimientos</h2></div>
        <div className="panel-cuerpo">{a.proximos.length === 0 ? <p className="suave">No hay mantenimientos programados.</p> : <ListaProximos filas={a.proximos} />}</div>
      </section>
      <section className="panel" aria-label="Trabajos recientes">
        <div className="panel-cabecera"><h2>Trabajos recientes</h2></div>
        <div className="panel-cuerpo">
          {a.partesRecientes.length === 0 ? <p className="suave">No hay partes visibles.</p> : (
            <ul className="lista-mis-partes">{a.partesRecientes.map((p) => <li key={p.id}><TarjetaParte p={p} volver={volverAqui} /></li>)}</ul>
          )}
        </div>
      </section>
      <section className="panel" aria-label="Documentación">
        <div className="panel-cabecera"><h2>Documentación</h2></div>
        <div className="panel-cuerpo"><Evidencias evidencias={a.documentos} vacio="No hay documentación publicada." /></div>
      </section>
      {solicitar && (
        <DialogoSolicitar activo={a} cerrar={() => setSolicitar(false)}
          alEnviar={(pt) => { setSolicitar(false); setEnviada(pt); d.recargar(); }} />
      )}
    </Shell>
  );
}

/**
 * F12.2 — «Solicitar mantenimiento» desde el equipo: qué le pasa, cuánto corre prisa y, si quiere, una foto. Centro,
 * equipo y cliente ya se saben. Queda una incidencia suya y un parte de trabajo abierto, que gestión asigna y planifica.
 */
function DialogoSolicitar({ activo: a, cerrar, alEnviar }: {
  activo: ActivoPublicoDetalle; cerrar: () => void; alEnviar: (pt: { id: string; codigo: string }) => void;
}): ReactNode {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [urgencia, setUrgencia] = useState('MEDIA');
  const [foto, setFoto] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<{ incidencia: { id: string }; parte: { id: string; codigo: string } }>(
        `/api/portal/mantenimiento/activos/${a.id}/solicitar`,
        { titulo: titulo.trim(), descripcion: descripcion.trim() || null, urgencia });
      if (foto !== null) {
        const fd = new FormData();
        fd.set('contexto', 'GENERAL');
        fd.set('archivo', foto);
        //  La foto va a SU incidencia (la solicitud ya está registrada aunque la foto fallase).
        await api.subir(`/api/portal/mantenimiento/incidencias/${r.incidencia.id}/evidencias`, fd).catch(() => undefined);
      }
      alEnviar(r.parte);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo="Solicitar mantenimiento" cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Solicitar mantenimiento" className="form-solicitar">
        <dl className="contexto-alta">
          <div><dt>Equipo</dt><dd>{a.nombre}{a.titularidad === 'EMPRESA' ? ` (${a.titularidadTexto})` : ''}</dd></div>
          <div><dt>Dónde</dt><dd>{a.centro.nombre}{a.ubicacion !== null ? ` · ${a.ubicacion}` : ''}</dd></div>
        </dl>
        <div className="campo">
          <label htmlFor="sol-titulo">¿Qué le pasa?</label>
          <input id="sol-titulo" required minLength={3} maxLength={200} value={titulo} placeholder="No enfría, hace ruido, pierde agua…" onChange={(ev) => setTitulo(ev.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="sol-descripcion">Cuéntanos más (opcional)</label>
          <textarea id="sol-descripcion" rows={3} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="sol-urgencia">¿Corre prisa?</label>
          <select id="sol-urgencia" value={urgencia} onChange={(ev) => setUrgencia(ev.target.value)}>
            {Object.entries(URGENCIAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="sol-foto">Foto (opcional)</label>
          <input id="sol-foto" type="file" accept="image/*,application/pdf" onChange={(ev) => setFoto(ev.target.files?.[0] ?? null)} />
        </div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando || titulo.trim().length < 3}>{enviando ? 'Enviando…' : 'Enviar solicitud'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------ próximos

export function PaginaPortalProximos(): ReactNode {
  const lista = useDatos(() => api.get<{ filas: PartePublico[] }>('/api/portal/mantenimiento/proximos'), []);
  return (
    <Shell migas={[...MIGAS, { texto: 'Próximos mantenimientos' }]} titulo="Próximos mantenimientos">
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? (
          <div className="panel"><Vacio titulo="No hay mantenimientos programados." /></div>
        ) : <ListaProximos filas={d.filas} />)}
      </Panel>
    </Shell>
  );
}

// ------------------------------------------------------------------ etiqueta QR (FASE 9.1-B)

/**
 * Etiqueta imprimible del activo para el CLIENTE, desde SU ficha pública
 * (mismo DTO, misma RLS: sólo sus activos). El QR codifica la MISMA URL que
 * la etiqueta interna (`/activos/:id`): al escanearla, cada rol llega a su
 * vista. Sólo código, nombre, centro y la marca; nada interno (ni número de
 * serie, ni técnicos, ni historial).
 */
export function PaginaPortalEtiquetaActivo(): ReactNode {
  const { id = '' } = useParams();
  const d = useDatos(() => api.get<ActivoPublicoDetalle>(`/api/portal/mantenimiento/activos/${encodeURIComponent(id)}`), [id]);
  if (d.cargando && d.datos === null) return <Cargando />;
  if (d.error !== null || d.datos === null) {
    return (
      <div className="etiqueta-activo-pagina">
        <Vacio titulo="Este activo no existe o no está disponible."><Link to={`${RAIZ}/activos`}>Volver a Activos</Link></Vacio>
      </div>
    );
  }
  const a = d.datos;
  return (
    <div className="etiqueta-activo-pagina">
      <div className="fila no-imprimir" style={{ marginBottom: 16 }}>
        <button className="principal" onClick={() => window.print()}>Imprimir</button>
        <Link to={`${RAIZ}/activos/${a.id}`}>Volver a la ficha</Link>
      </div>
      <div className="etiqueta-activo" aria-label={`Etiqueta del activo ${a.codigo}`}>
        <CodigoQr texto={urlFicha(a.id)} tamano={170} titulo={`QR del activo ${a.codigo}`} />
        <div className="etiqueta-activo-texto">
          <div className="mono etiqueta-activo-codigo">{a.codigo}</div>
          <div className="etiqueta-activo-nombre">{a.nombre}</div>
          <div>{a.centro.nombre}</div>
          {a.titularidad === 'EMPRESA' && <div className="pequena">{a.titularidadTexto}</div>}
          <div className="pequena suave">NeatOS · Escanea para ver el activo o avisar de una incidencia</div>
        </div>
      </div>
    </div>
  );
}
